import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isNil, omit, throttle } from 'lodash';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import DatePickerMulti from 'components/date-picker/date-picker-multi/DatePickerMultiLazy';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import DeliverableTag from '../deliverable-tag/DeliverableTag';
import AttachmentsWithViewDelete from 'components/attachments-with-view-delete/AttachmentsWithViewDelete';
import DeliverableNoMixedCampaignContent from '../deliverable-no-mixed-campaign-content/DeliverableNoMixedCampaignContent';
import DeliverableYTWarning from '../deliverable-yt-warning/DeliverableYTWarning';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { isEnableEntertainmentVideoSelector } from 'selectors/enableEntertainmentVideo';
import { uploadAssets } from 'modules/deliverables/reducers/deliverableMediaAttachments';

import { __t, applyTranslations } from 'utils/language';
import {
  isGreaterThanZero,
  isRequired,
  isValidDateRange,
  maxLength
} from 'utils/validators';
import {
  convertListToHumanReadable,
  copyTextToClipboard
} from 'utils/helper/common';
import { isInfluencerRealm } from 'utils/account';
import {
  DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER,
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  MAX_DELIVERABLE_DESCRIPTION_LENGTH,
  MAX_DELIVERABLE_LOCATION_LENGTH,
  MAX_NAME_LENGTH,
  MAX_TAG_NAME_LENGTH_NEW,
  SUPPORTED_MIMETYPES,
  canAddDeliverableToSubcampaigns,
  getAvailableDeliverableTypesForChannels,
  getContentApprovalSupportedTaskTypes,
  getInsightsProvisionStartDate,
  isEntertainmentVideo
} from 'utils/deliverables';
import { DISABLED_OUTFIT_STATUSES } from 'utils/realm-outfits';
import {
  hasYTWarningModalBeingShown,
  setYTWarningModalBeingShown
} from 'modules/deliverables/utils/yt-modal';

import type {
  CampaignAsset,
  Deliverable,
  DeliverableType,
  MixedSelectDropdownItem,
  Subcampaign,
  TextEntry
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./deliverable-edit-common-v2.i18n.json'));

import './deliverable-edit-common-v2.scss';

type OwnProps = {
  campaignId: number,
  creatorId: number,
  subcampaignId: number,
  deliverable: Deliverable,
  availableChannels: Array<string>,
  offerDeliverables: Array<Deliverable>,
  onEdit: (a: Partial<Deliverable>) => void
};

type StateProps = {
  subcampaignsList: Array<Subcampaign>,
  isSCP: boolean,
  isEntertainmentVideoEnabled: boolean
};

type DispatchProps = {
  dispatchShowConfirmationModal: (
    params: {
      title: string,
      message?: string,
      confirmButtonLabel?: string,
      cancelButtonLabel?: string,
      innerContent?: React.ReactNode,
      onConfirm: () => Promise<void>,
      onCancel?: () => Promise<void>
    }
  ) => Promise<void>,
  dispatchHideConfirmationModal: () => Promise<void>,
  dispatchUploadAssets: (files: Array<File>) => Promise<Array<CampaignAsset>>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  deliverable: Deliverable,
  isDefaultName: boolean,
  hasInsightsProvisionTimelineChanged: boolean
};

const MSG_KEY_COMMON = 'collabary_common';

class DeliverableEditCommonForm extends React.PureComponent<Props, State> {
  throttledHandleEdit: () => void;

  constructor(props: Props) {
    super(props);

    const { deliverable } = props;
    const deliverableType = deliverable.deliverableType || DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER;

    this.state = {
      hasInsightsProvisionTimelineChanged: false,
      deliverable: {
        ...deliverable,
        name: (deliverable && deliverable.name) || __t(`${MSG_KEY_COMMON}.types.${deliverableType}.singular`),
        requiresApproval: !!deliverable.requiresApproval,
        captionApprovalRequired: !!deliverable.captionApprovalRequired
      },
      isDefaultName: isNil(deliverable) || isNil(deliverable.name)
        || !isNil(deliverable.deliverableType) && deliverable.name === __t(`${MSG_KEY_COMMON}.types.${deliverable.deliverableType}.singular`)
    };

    this.throttledHandleEdit = throttle(this.handleEdit, 1000);
  }

  showYTWarningModal(): Promise<void> {
    const { dispatchHideConfirmationModal, dispatchShowConfirmationModal } = this.props;

    return new Promise((resolve) => {
      dispatchShowConfirmationModal({
        title: __t('deliverable_edit_common_v2.yt_warning_modal_header'),
        confirmButtonLabel: __t('deliverable_edit_common_v2.no_mixed_campaigns.button'),
        innerContent: <DeliverableYTWarning />,
        onConfirm: () => {
          resolve();
          return dispatchHideConfirmationModal();
        }
      });
    });
  }

  getIsTaskTypeOtherStatus(): boolean {
    return this.props.deliverable.deliverableType === DELIVERABLE_TYPE_OTHER;
  }

  handleTypeChanged = (value: DeliverableType): void => {
    const { deliverable, isDefaultName } = this.state;

    const defaultName = __t(`${MSG_KEY_COMMON}.types.${value}.singular`);
    const useDefaultName = isDefaultName || isEmpty(deliverable.name) || value === DELIVERABLE_TYPE_CONTENT_PRODUCTION;
    const name = useDefaultName ? defaultName : deliverable.name;
    const isOtherTaskType = value === DELIVERABLE_TYPE_OTHER;
    let resetDeliverablesFields = {};

    if (isOtherTaskType) {
      resetDeliverablesFields = {
        captionApprovalRequired: false,
        contentApprovalTimeline: {
          startDate: '',
          endDate: ''
        },
        insightsProvisionTimeline: {
          startDate: '',
          endDate: ''
        },
        requiresApproval: false
      };
    } else if (value === DELIVERABLE_TYPE_ZALANDO_OUTFIT) {
      resetDeliverablesFields = {
        numOutfits: null
      };
    }

    this.setState({
      deliverable: {
        ...deliverable,
        ...resetDeliverablesFields,
        deliverableType: value,
        name,
        requiresApproval: value === DELIVERABLE_TYPE_ZALANDO_OUTFIT || isEntertainmentVideo(value)
      },
      isDefaultName: useDefaultName
    }, this.handleEdit);
  };

  handleTypeChange = async (value: DeliverableType): Promise<void> => {
    const {
      campaignId,
      creatorId,
      deliverable: deliverableInProps,
      subcampaignId,
      offerDeliverables,
      subcampaignsList,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;
    const { deliverable } = this.state;

    if (deliverable.deliverableType === value) {
      return;
    }

    const offerDeliverablesToCheck = offerDeliverables.filter(entity => entity.id !== deliverableInProps.id);
    const shouldShowYTWarning = value === DELIVERABLE_TYPE_YOUTUBE_VIDEO && !hasYTWarningModalBeingShown(campaignId, creatorId);
    const hasSubmittedOutfits = (deliverable.outfits ?? []).length > 0;
    const hasApprovedOutfits = (deliverable.outfits ?? [])
      .some(o => DISABLED_OUTFIT_STATUSES.includes(o.status));

    if (shouldShowYTWarning) {
      setYTWarningModalBeingShown(campaignId, creatorId);
    }

    if (!canAddDeliverableToSubcampaigns(value, offerDeliverablesToCheck, subcampaignsList.filter(s => s.id !== subcampaignId))) {
      dispatchShowConfirmationModal({
        title: __t('deliverable_edit_common_v2.no_mixed_campaigns.title'),
        confirmButtonLabel: __t('deliverable_edit_common_v2.no_mixed_campaigns.button'),
        innerContent: (
          <DeliverableNoMixedCampaignContent />
        ),
        onConfirm: () => dispatchHideConfirmationModal()
      });
      return;
    }

    if (deliverableInProps.post || (deliverableInProps.postAttachments || []).length > 0) {
      dispatchShowConfirmationModal({
        title: __t('deliverable_edit_common_v2.edit_task_type_confirmation.title'),
        message: __t('deliverable_edit_common_v2.edit_task_type_confirmation.message'),
        onConfirm: () => {
          this.handleTypeChanged(value);
          return dispatchHideConfirmationModal();
        },
        onCancel: () => dispatchHideConfirmationModal()
      });

      return;
    }

    if (hasApprovedOutfits) {
      dispatchShowConfirmationModal({
        title: __t('deliverable_edit_common_v2.disabled_edit_outfits_task_type.title'),
        message: __t('deliverable_edit_common_v2.disabled_edit_outfits_task_type.message', {
          outfitStatuses: convertListToHumanReadable(DISABLED_OUTFIT_STATUSES)
        }),
        onConfirm: () => dispatchHideConfirmationModal(),
        confirmButtonLabel: __t('collabary_common.action_button.close')
      });

      return;
    }

    if (hasSubmittedOutfits) {
      dispatchShowConfirmationModal({
        title: __t('deliverable_edit_common_v2.edit_outfits_task_type_confirmation.title'),
        message: __t('deliverable_edit_common_v2.edit_outfits_task_type_confirmation.message'),
        onConfirm: () => {
          this.handleTypeChanged(value);
          return dispatchHideConfirmationModal();
        },
        onCancel: () => dispatchHideConfirmationModal(),
        confirmButtonLabel: __t('deliverable_edit_common_v2.edit_outfits_task_type_confirmation.yes'),
        cancelButtonLabel: __t('deliverable_edit_common_v2.edit_outfits_task_type_confirmation.no')
      });

      return;
    }

    if (shouldShowYTWarning) {
      await this.showYTWarningModal();
    }

    this.handleTypeChanged(value);
  };

  handleNameChange = (event: React.SyntheticEvent<TextEntry>): void => {
    const { deliverable } = this.state;
    const target = event.currentTarget;
    const value = target.value || '';

    this.setState({
      deliverable: {
        ...deliverable,
        name: value
      },
      isDefaultName: deliverable.name === value
    }, this.throttledHandleEdit);
  };

  handleChangeOutfitsNumber = (event: React.SyntheticEvent<TextEntry>): void => {
    const { deliverable } = this.state;
    const target = event.currentTarget;
    let value = parseInt(target.value ?? '', 10) || 0;

    if (value > 1000) {
      value = 999;
    }

    this.setState({
      deliverable: {
        ...deliverable,
        numOutfits: value
      }
    }, this.throttledHandleEdit);
  };

  handleLocationChange = (event: React.SyntheticEvent<TextEntry>): void => {
    const { deliverable } = this.state;
    const target = event.currentTarget;
    const value = target.value || '';

    this.setState({
      deliverable: {
        ...deliverable,
        location: value
      }
    }, this.throttledHandleEdit);
  };

  handleDateChange = (
    dateType: 'contentApprovalTimeline' | 'contentGoLiveTimeline' | 'insightsProvisionTimeline',
    startDate?: string | null,
    endDate?: string | null
  ): void => {
    const { deliverable, hasInsightsProvisionTimelineChanged } = this.state;
    const { insightsProvisionTimeline } = deliverable;
    let prefilledInsightsProvision = { ...insightsProvisionTimeline };
    const isContentApprovalTimeline = dateType === 'contentGoLiveTimeline';
    const isInsightsProvisionTimeline = dateType === 'insightsProvisionTimeline';

    if (!this.getIsTaskTypeOtherStatus() && isContentApprovalTimeline && endDate && !hasInsightsProvisionTimelineChanged) {
      const insightsProvisionDate = getInsightsProvisionStartDate(deliverable.deliverableType, endDate);
      prefilledInsightsProvision = {
        startDate: insightsProvisionDate,
        endDate: insightsProvisionDate
      };
    }

    this.setState((prevState) => ({
      deliverable: {
        ...prevState.deliverable,
        [dateType]: {
          startDate: startDate || '',
          endDate: endDate || ''
        },
        ...(isContentApprovalTimeline ? { insightsProvisionTimeline: { ...prefilledInsightsProvision } } : {})
      },
      hasInsightsProvisionTimelineChanged: isInsightsProvisionTimeline
    }), this.handleEdit);
  };

  handleDescriptionChange = (value: string): void => {
    const { deliverable } = this.state;

    this.setState({
      deliverable: {
        ...deliverable,
        description: value
      }
    }, this.handleEdit);
  };

  handleAttachmentsChange = (guidelinesAttachments: Array<CampaignAsset>): void => {
    const { deliverable } = this.state;

    this.setState({
      deliverable: {
        ...deliverable,
        guidelinesAttachments
      }
    }, this.handleEdit);
  };

  handleContentApprovalRequiredChange = (value: boolean, key: 'requiresApproval' | 'captionApprovalRequired'): void => {
    const { deliverable } = this.state;

    this.setState({
      deliverable: {
        ...deliverable,
        [key]: value
      }
    }, this.handleEdit);
  };

  handleEdit = () => {
    const { onEdit } = this.props;
    const {
      deliverableType,
      name,
      contentApprovalTimeline,
      contentGoLiveTimeline,
      insightsProvisionTimeline,
      description,
      location,
      captionApprovalRequired,
      guidelinesAttachments,
      requiresApproval,
      numOutfits
    } = this.state.deliverable;

    onEdit({
      deliverableType,
      name,
      location,
      contentApprovalTimeline,
      contentGoLiveTimeline,
      insightsProvisionTimeline,
      captionApprovalRequired,
      requiresApproval,
      description,
      guidelinesAttachments,
      numOutfits
    });
  };

  handleDeleteAttachment = (attachmentId: string | number) => {
    const { deliverable } = this.props;
    const { guidelinesAttachments } = deliverable;

    const newguidelinesAttachments = guidelinesAttachments?.filter(attachment => attachment.id != attachmentId);

    this.setState((prevState) => ({
      ...prevState,
      deliverable: {
        ...prevState.deliverable,
        guidelinesAttachments: newguidelinesAttachments
      }
    }), this.handleEdit);
  };

  handleCopy = (event: React.SyntheticEvent<HTMLElement>) => {
    const target = event.currentTarget;

    copyTextToClipboard(`[data-copy-key="${target.dataset.keyToCopy}"]`);
  };

  saveAttachments = (files: Array<File>) => {
    const { dispatchUploadAssets, deliverable } = this.props;

    dispatchUploadAssets(files)
      .then((guidelinesAttachments) => {
        const attachments = (guidelinesAttachments || []).map(attachment => {
          if (typeof attachment.id === 'string') {
            return omit(attachment, ['id']);
          }

          return attachment;
        });
        // @ts-ignore temp id not needed to BE
        this.handleAttachmentsChange([...(deliverable.guidelinesAttachments || []), ...attachments]);
      });
  };

  getDeliverableTypes(): Array<DeliverableType> {
    const {
      availableChannels,
      isSCP,
      isEntertainmentVideoEnabled
    } = this.props;

    const available = getAvailableDeliverableTypesForChannels(availableChannels || [], isEntertainmentVideoEnabled, true);

    return isSCP
      ? available
      : available.filter(v => v !== DELIVERABLE_TYPE_ZALANDO_OUTFIT);
  }

  getDeliverableTypeOptions(): Array<MixedSelectDropdownItem> {
    const { deliverable } = this.state;
    const channels = this.getDeliverableTypes();

    return channels.map(channelType => ({
      label: __t(`${MSG_KEY_COMMON}.types.${channelType}.singular`),
      identifier: channelType,
      value: channelType,
      isDisabled: false,
      isFocused: false,
      isSelected: deliverable.deliverableType === channelType
    }));
  }

  renderTypeChooser(): React.ReactNode {
    const channels = this.getDeliverableTypeOptions();

    return (
      <FormField
        label={__t('deliverable_edit_common_v2.label_type_of_task')}
        labelType="highlighted"
        labelSize="collapsed"
        showValidationIcon={false}
        direction="column"
        fontSize="small"
      >
        <MixedSelectDropdown
          name="deliverable-details-edit-tag-color"
          items={channels} // @ts-ignore selectedItem.value will always be string
          onItemsToggle={(updatedItems, data, selectedItem) => this.handleTypeChange(selectedItem.value)}
        />
      </FormField>
    );
  }

  renderCopyControl(deliverable: Deliverable, name: string): React.ReactNode {
    return (
      <Tooltip
        message={__t(`${MSG_KEY_COMMON}.basics.copy`)}
        width="micro"
      >
        <div className="deliverable-details-form-v2__tags-copy">
          <Tooltip
            message={__t(`${MSG_KEY_COMMON}.basics.copied`)}
            width="micro"
            trigger="click"
          >
            <SVGDynamicIcon
              height={16}
              width={16}
              name="duplicate-custom-fg"
              customColor="grey"
              data={{ 'key-to-copy': `${deliverable.id}-${name}-field-to-copy` }}
              onClick={this.handleCopy}
            />
          </Tooltip>
        </div>
      </Tooltip>
    );
  }

  renderNameField(): React.ReactNode {
    const { deliverable } = this.state;

    return (
      <div className="deliverable-edit-common-v2__task-title">
        <FormField
          label={__t('deliverable_edit_common_v2.name_label')}
          labelType="highlighted"
          showValidationIcon={false}
          showRequiredMark
          direction="column"
          fontSize="small"
          validators={[
            isRequired(deliverable.name),
            maxLength(deliverable.name, MAX_NAME_LENGTH)
          ]}
        >
          {
            deliverable.deliverableType === DELIVERABLE_TYPE_CONTENT_PRODUCTION ?
              <span className="deliverable-edit-common-v2__field-value">
                {deliverable.name}
              </span> :
              <Input
                value={deliverable.name}
                width="responsive"
                charCount={{
                  curCount: deliverable.name.length,
                  maxChars: MAX_NAME_LENGTH
                }}
                onChange={this.handleNameChange}
                placeholder={__t('deliverable_edit_common_v2.name_placeholder')}
              />
          }
        </FormField>
      </div>
    );
  }

  renderOtherTaskContentApprovalSection(): React.ReactNode {
    const { deliverable } = this.state;
    const { id, requiresApproval } = deliverable;

    return (
      <FormField
        showValidationIcon={false}
        label={__t(`${MSG_KEY_COMMON}.types.content_production.singular`)}
        labelType="highlighted"
        direction="column"
        fontSize="small"
        className="deliverable-edit-common-v2__other-task-content-approval"
      >
        <InputCheckbox
          id={`deliverable-edit-common-v2-content-approval-other-${id}`}
          name={`deliverable-edit-common-v2-content-approval-other-${id}`}
          label={__t('deliverable_edit_common_v2.label_content_production_required')}
          value={String(requiresApproval)}
          checked={requiresApproval}
          onChange={({ currentTarget }) => this.handleContentApprovalRequiredChange(currentTarget.checked, 'requiresApproval')}
        />
      </FormField>
    );
  }

  renderDatesInput(dateType: 'contentApprovalTimeline' | 'contentGoLiveTimeline' | 'insightsProvisionTimeline'): React.ReactNode {
    const { deliverable } = this.state;

    return (
      <DatePickerMulti
        startDateName={`${dateType}-startDate-${deliverable.id}`}
        startDate={deliverable[dateType].startDate}
        endDateName={`${dateType}-endDate-${deliverable.id}`}
        endDate={deliverable[dateType].endDate}
        onChange={(startDate, endDate) => this.handleDateChange(dateType, startDate, endDate)}
      />
    );
  }

  renderOutfitsNumber(): React.ReactNode {
    const { deliverable } = this.state;

    return (
      <div>
        <FormField
          showRequiredMark
          label={__t('deliverable_edit_common_v2.label_outfits_number')}
          labelType="highlighted"
          showValidationIcon={false}
          direction="column"
          fontSize="small"
          validators={[isGreaterThanZero(deliverable.numOutfits)]}
        >
          <Input
            value={String(deliverable.numOutfits ?? 0)}
            width="responsive"
            onChange={this.handleChangeOutfitsNumber}
            placeholder={__t('deliverable_edit_common_v2.outfits_number_placheholder')}
            className="deliverable-edit-common-v2__num-outfits-input"
          />
        </FormField>
      </div>
    );
  }

  renderDates(): React.ReactNode {
    const { isSCP } = this.props;
    const { deliverable } = this.state;

    const {
      id,
      requiresApproval,
      captionApprovalRequired,
      contentApprovalTimeline,
      contentGoLiveTimeline,
      insightsProvisionTimeline,
      deliverableType
    } = deliverable;

    const isZalandoOutfit = deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT;
    const isEntertainmentVideoDeliverable = isEntertainmentVideo(deliverableType);

    return (
      <React.Fragment>
        {
          getContentApprovalSupportedTaskTypes(isSCP).includes(deliverableType) && (
            <div className="deliverable-edit-common-v2__content-approval-form-container">
              <div className="deliverable-edit-common-v2__content-approval-form-label">
                {__t(`${MSG_KEY_COMMON}.types.content_production.singular`)}
              </div>
              <div className="deliverable-edit-common-v2__content-approval">
                <InputCheckbox
                  id={`deliverable-edit-common-v2-content-approval-${id}`}
                  name={`deliverable-edit-common-v2-content-approval-${id}`}
                  label={__t('deliverable_edit_common_v2.label_content_production_required')}
                  value={String(requiresApproval)}
                  checked={requiresApproval}
                  disabled={isZalandoOutfit || isEntertainmentVideoDeliverable}
                  onChange={({ currentTarget }) => this.handleContentApprovalRequiredChange(currentTarget.checked, 'requiresApproval')}
                />
                {
                  !isZalandoOutfit && !isEntertainmentVideoDeliverable && requiresApproval && (
                    <InputCheckbox
                      id={`deliverable-edit-common-v2-content-approval-with-caption-${id}`}
                      name={`deliverable-edit-common-v2-content-approval-with-caption-${id}`}
                      label={__t('deliverable_edit_common_v2.label_content_production_with_caption_approval')}
                      value={String(captionApprovalRequired)}
                      checked={captionApprovalRequired}
                      onChange={({ currentTarget }) => this.handleContentApprovalRequiredChange(currentTarget.checked, 'captionApprovalRequired')}
                      containerClass="deliverable-edit-common-v2__content-approval-with-caption"
                    />
                  )
                }
              </div>
              {
                requiresApproval && (
                  <FormField
                    showValidationIcon={false}
                    direction="column"
                    fontSize="small"
                    validators={[
                      isRequired(contentApprovalTimeline.startDate),
                      isRequired(contentApprovalTimeline.endDate),
                      isValidDateRange(contentApprovalTimeline.startDate, contentApprovalTimeline.endDate)
                    ]}
                  >
                    <div className="deliverable-edit-common-v2__dates deliverable-edit-common-v2__dates--conditional-date">
                      {this.renderDatesInput('contentApprovalTimeline')}
                    </div>
                  </FormField>
                )
              }
            </div>
          )
        }
        {
          !isZalandoOutfit && (
            <React.Fragment>
              <FormField
                label={__t(`deliverable_edit_common_v2.label_${this.getIsTaskTypeOtherStatus() ? 'timeframe' : 'content_live_dates'}`)}
                labelType="highlighted"
                showRequiredMark
                showValidationIcon={false}
                direction="column"
                fontSize="small"
                validators={[
                  isRequired(contentGoLiveTimeline.startDate),
                  isRequired(contentGoLiveTimeline.endDate),
                  isValidDateRange(contentGoLiveTimeline.startDate, contentGoLiveTimeline.endDate),
                  ...((requiresApproval && contentApprovalTimeline.startDate) ?
                    [isValidDateRange(contentApprovalTimeline.startDate,
                      contentGoLiveTimeline.startDate,
                      __t('deliverable_edit_common_v2.errors.content_go_live_date_error'))] : [])
                ]}
              >
                <div className="deliverable-edit-common-v2__dates">
                  {this.renderDatesInput('contentGoLiveTimeline')}
                </div>
              </FormField>
              {
                deliverableType === DELIVERABLE_TYPE_OTHER &&
                this.renderOtherTaskContentApprovalSection()
              }
              {
                (!this.getIsTaskTypeOtherStatus() && !isEntertainmentVideoDeliverable) && (
                  <div>
                    <FormField
                      label={__t('deliverable_edit_common_v2.label_insights_provision_dates')}
                      labelType="highlighted"
                      showRequiredMark
                      showValidationIcon={false}
                      direction="column"
                      fontSize="small"
                      validators={[
                        isRequired(insightsProvisionTimeline.startDate),
                        isRequired(insightsProvisionTimeline.endDate),
                        isValidDateRange(insightsProvisionTimeline.startDate, insightsProvisionTimeline.endDate),
                        ...(contentGoLiveTimeline.startDate ?
                          [isValidDateRange(contentGoLiveTimeline.startDate,
                            insightsProvisionTimeline.startDate,
                            __t('deliverable_edit_common_v2.errors.insights_provision_date_error'))] : [])
                      ]}
                    >
                      <div className="deliverable-edit-common-v2__dates">
                        {this.renderDatesInput('insightsProvisionTimeline')}
                      </div>
                    </FormField>
                  </div>
                )
              }
            </React.Fragment>
          )
        }
        {isZalandoOutfit && this.renderOutfitsNumber()}
      </React.Fragment>
    );
  }

  renderLocation(): React.ReactNode {
    const { deliverable } = this.state;

    return (
      <div className="deliverable-edit-common-v2__form-section deliverable-edit-common-v2__location">
        <FormField
          label={__t('deliverable_edit_common_v2.location_label')}
          labelType="highlighted"
          showValidationIcon={false}
          tooltipMessage={__t('deliverable_edit_common_v2.location_tooltip')}
          direction="column"
          fontSize="small"
          validators={[
            maxLength(deliverable.location, MAX_DELIVERABLE_LOCATION_LENGTH)
          ]}
        >
          <div
            className="deliverable-edit-common-v2__input"
            data-copy-key={`${deliverable.id}-location-field-to-copy`}
          >
            <Input
              width="responsive"
              name="location"
              value={deliverable.location || ''}
              autoComplete="off"
              onChange={this.handleLocationChange}
            />
            {this.renderCopyControl(deliverable, 'location')}
          </div>
        </FormField>
      </div>
    );
  }

  renderDescription(): React.ReactNode {
    const { deliverable } = this.state;
    const deliverableDescription = deliverable.description || '';

    return (
      <div className="deliverable-edit-common-v2__form-section">
        <FormField
          label={__t('deliverable_edit_common_v2.description_label')}
          labelType="highlighted"
          showValidationIcon={false}
          direction="column"
          fontSize="small"
          validators={[
            maxLength(deliverable.description, MAX_DELIVERABLE_DESCRIPTION_LENGTH)
          ]}
        >
          <RichTextEditor
            key={deliverableDescription}
            canEdit
            value={deliverableDescription}
            onChange={this.handleDescriptionChange}
            charCount={{
              maxChars: MAX_DELIVERABLE_DESCRIPTION_LENGTH
            }}
            onUpload={this.saveAttachments}
            supportedMimetypes={SUPPORTED_MIMETYPES}
            uploadTooltipMessage={__t('deliverable_edit_common_v2.upload_btn_tooltip')}
          />
        </FormField>
      </div>
    );
  }

  render() {
    const { onEdit, deliverable: { tag, guidelinesAttachments, id } } = this.props;

    return (
      <div className="deliverable-edit-common-v2__container">
        <div className="deliverable-edit-common-v2__form-section deliverable-edit-common-v2__form-section--grid-layout">
          {this.renderTypeChooser()}
          {this.renderNameField()}
        </div>
        <div className="deliverable-edit-common-v2__form-section deliverable-edit-common-v2__form-section--grid-layout">
          <DeliverableTag
            onEdit={(deliverableTag) => onEdit(deliverableTag)}
            deliverableTag={tag}
            hasParent={false}
            hasFixedWidthColorElement={false}
            maxTagNameLength={MAX_TAG_NAME_LENGTH_NEW}
          />
        </div>
        <div className="deliverable-edit-common-v2__form-section deliverable-edit-common-v2__form-section--grid-layout">
          {this.renderDates()}
        </div>
        {this.renderDescription()}
        {
          !!guidelinesAttachments?.length && (
            <AttachmentsWithViewDelete
              attachments={guidelinesAttachments}
              onDelete={(attachmentId: number | string) => this.handleDeleteAttachment(attachmentId)}
              uniqueId={id}
            />
          )
        }
        {
          this.getIsTaskTypeOtherStatus() &&
            this.renderLocation()
        }
      </div>
    );
  }
}

const mapStateToProps = ({ account, campaignSubcampaigns, runtimeConfig }): StateProps => ({
  subcampaignsList: campaignSubcampaigns.list,
  isEntertainmentVideoEnabled: isEnableEntertainmentVideoSelector({ runtimeConfig, account }),
  isSCP: isInfluencerRealm(account.roles)
      && runtimeConfig.config.zalandoOutfitTaskTypeEnabled
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: (props) => dispatch(showConfirmModal(props)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),
  dispatchUploadAssets: (files: Array<File>) => dispatch(uploadAssets(files))
});

export default connect(mapStateToProps, mapDispatchToProps)(DeliverableEditCommonForm);
